import { useState, useEffect } from 'react';
import './App.css';
import LoginForm from './components/LoginForm';
import HomePage from './pages/HomePage';
import SchoolDetailsPage from './pages/SchoolDetailsPage';
import useAuth from './hooks/useAuth';
import useSchools from './hooks/useSchools';
import useSchoolUsers from './hooks/useSchoolUsers';
import usePrintingProgress from './hooks/usePrintingProgress';
import useDashboardSummary from './hooks/useDashboardSummary';
import { populateNfcCardsRequest } from './services/apiClient';

function App() {
  const auth = useAuth();
  const schoolsState = useSchools(auth.token);
  const usersState = useSchoolUsers(auth.token);
  const { clearSelection } = usersState;
  const [view, setView] = useState('home');
  const [notification, setNotification] = useState(null);

  const permissions = {
    canConfirmPrinting: auth.hasRole ? auth.hasRole('checkPrintingCard') : false,
    canAddEmail: auth.hasRole ? auth.hasRole('checkAddEmail') : false,
    canPaperCat: auth.hasRole ? auth.hasRole('checkPaperCat') : false,
    canEndAll: auth.hasRole ? auth.hasRole('checkEndAll') : false,
    canViewDashboard: auth.hasRole ? auth.hasRole('Dashboard') : false,
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const printingProgressState = usePrintingProgress(
    auth.token,
    permissions.canConfirmPrinting
  );
  const addEmailProgressState = usePrintingProgress(
    auth.token,
    permissions.canAddEmail,
    'add-email'
  );
  const paperCatProgressState = usePrintingProgress(
    auth.token,
    permissions.canPaperCat,
    'paper-cat'
  );
  const endAllProgressState = usePrintingProgress(
    auth.token,
    permissions.canEndAll,
    'end-all'
  );
  const dashboardSummaryState = useDashboardSummary(
    auth.token,
    permissions.canViewDashboard
  );

  useEffect(() => {
    if (!auth.token) {
      setView('home');
      clearSelection();
    }
  }, [auth.token, clearSelection]);

  const handleSchoolSelect = async (school) => {
    if (!school) {
      return;
    }
    
    // الانتقال فوراً لتفاصيل المدرسة
    usersState.selectSchool(school);
    setView('school-details');

    // استدعاء دالة التحديث في الخلفية
    try {
      await populateNfcCardsRequest(auth.token);
      showNotification('تم تحديث بيانات البطاقات بنجاح.', 'success');
    } catch (error) {
      console.error('Populate Error:', error);
      showNotification('فشل تحديث بيانات البطاقات.', 'error');
    }
  };
  
  const handleBackToHome = () => {
    setView('home');
    usersState.clearSelection();
  };

  if (auth.token) {
    return (
      <div className="app-layout">
        {view === 'home' && (
          <HomePage
            user={auth.user}
            onLogout={auth.logout}
            schoolsState={schoolsState}
            onSchoolSelect={handleSchoolSelect}
            canManagePrinting={permissions.canConfirmPrinting}
            printingProgressState={printingProgressState}
            canManageAddEmail={permissions.canAddEmail}
            addEmailProgressState={addEmailProgressState}
            canManagePaperCat={permissions.canPaperCat}
            paperCatProgressState={paperCatProgressState}
            canManageEndAll={permissions.canEndAll}
            endAllProgressState={endAllProgressState}
            canViewDashboard={permissions.canViewDashboard}
            dashboardSummaryState={dashboardSummaryState}
          />
        )}
        {view === 'school-details' && usersState.selectedSchool && (
          <SchoolDetailsPage
            user={auth.user}
            selectedSchool={usersState.selectedSchool}
            usersState={usersState}
            permissions={permissions}
            onBack={handleBackToHome}
            onLogout={auth.logout}
          />
        )} 
        {notification && (
          <div className={`app-notification ${notification.type}`}>
            {notification.message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app-layout">
      <div className="card">
        <LoginForm onSubmit={auth.login} status={auth.status} />
      </div>
    </div>
  );
}

export default App;
